import { useEffect, useRef, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { Division, Button, Heading, Paragraph } from "@ir/ui";
import "./confirm-dialog.scss";

type ConfirmDialogOptions = {
  title?: string;
  header?: ReactNode;
  message: string;
  confirmText?: string;
  confirmButtonVariant?: "solid" | "outline";
  confirmButtonColor?: "primary" | "grey";
  cancelText?: string;
  cancelButtonVariant?: "solid" | "outline";
  cancelButtonColor?: "primary" | "grey";
  alertOnly?: boolean;
  alertText?: string;
};

export function useConfirm() {
  const containerRef = useRef<HTMLElement | null>(null);
  const resolverRef = useRef<(value: boolean) => void>(() => false);

  useEffect(() => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    containerRef.current = container;
    return () => {
      container.remove();
    };
  }, []);

  const confirm = (options: ConfirmDialogOptions): Promise<boolean> => {
    const {
      confirmText = "Confirm",
      confirmButtonVariant = "solid",
      confirmButtonColor = "primary",
      cancelText = "Cancel",
      cancelButtonVariant = "outline",
      cancelButtonColor = "grey",
      alertOnly = false,
      alertText = "OK",
    } = options;
    return new Promise<boolean>(resolve => {
      if (!containerRef.current) return resolve(false);
      const root = createRoot(containerRef.current);
      resolverRef.current = resolve;

      const cleanup = () => {
        root.unmount();
      };

      const handleResult = (result: boolean) => {
        cleanup();
        resolverRef.current?.(result);
      };

      root.render(
        <Division className="confirm-dialog-backdrop">
          <Division className="confirm-dialog">
            <Division className="px-3 pt-3">
              {options.title && <Heading className="text-grey-700 font-bold m-0 confirm-dialog-title">{options.title}</Heading>}
            </Division>
            {options.header && <Division className="px-3">{options.header}</Division>}
            <Division className="p-3">
              <Paragraph
                className="m-0 text-md text-grey-600"
                style={alertOnly ? { whiteSpace: "pre-line" } : undefined}
              >
                {options.message}
              </Paragraph>
            </Division>
            <Division className="p-3 actions">
              {alertOnly ? (
                <Button variant="solid" color="primary" className="confirm flex-1 m-0" onPress={() => handleResult(false)}>
                  {alertText}
                </Button>
              ) : (
                <>
                  <Button variant={cancelButtonVariant} color={cancelButtonColor} className="cancel flex-1" onPress={() => handleResult(false)}>
                    {cancelText}
                  </Button>
                  <Button variant={confirmButtonVariant} color={confirmButtonColor} className="confirm flex-1 m-0" onPress={() => handleResult(true)}>
                    {confirmText}
                  </Button>
                </>
              )}
            </Division>
          </Division>
        </Division>
      );
    });
  };

  return confirm;
}
